// Copyright (c) 2026 Goffy. All Rights Reserved. This file belongs to Goffy.

import { buildDiscoverUrl, isProdBackend } from './jobLogsUrl';

/**
 * Where an analysis can be looked at from outside the app: its logs in Kibana, and its
 * numbers in Grafana.
 *
 * An analysis crosses four services and a couple of hours before a hole shows a bunker, and
 * every question about one ("did it fetch or reuse?", "why fewer greens than last week?",
 * "which step took the time?") is answered somewhere other than this screen. These are the
 * doors, built so a person lands on the right filter rather than on an empty Discover they
 * then have to hand-query.
 *
 * Every run-specific view is anchored on the run the caller names — the newest by default,
 * since that is the one whose answer is currently on the course, or the one a person picked
 * on the status page. Older runs also stay reachable through the course-wide views.
 */

/**
 * Grafana, via Caddy — the same choice the Kibana default makes in jobLogsUrl.ts, for the same
 * reason: a worker built from the working tree ships its metrics to the local collector, a
 * released one to the estate's, so which Grafana has ever seen the run follows the stack this
 * app is talking to. EXPO_PUBLIC_GRAFANA_BASE_URL overrides both.
 */
const PROD_GRAFANA_BASE_URL = 'http://grafana-prod.goffy.localhost';

const DEV_GRAFANA_BASE_URL = 'http://grafana-dev.goffy.localhost';

/** Dashboard uids, fixed in deploy/grafana-provisioning/dashboards/analysis-*.json. */
export const GRAFANA_DASHBOARDS = {
  overview: { uid: 'goffy-analysis-overview', slug: 'goffy-analysis-overview' },
  course: { uid: 'goffy-analysis-course', slug: 'goffy-analysis-course' },
  run: { uid: 'goffy-analysis-run', slug: 'goffy-analysis-run' },
} as const;

/** How far back a log view opens. Acquisition alone can run to a couple of hours. */
const LOG_LOOKBACK = 'now-7d';


export interface AnalysisLinkTarget {
  courseId: string;
  /** The newest run's id, when the course has been analysed at all. */
  runId?: string | null;
  /**
   * The correlation id that run carried. Present only for runs started after the id began
   * being stored; a course whose newest run predates that falls back to the run-id view,
   * which is narrower but never wrong.
   */
  correlationId?: string | null;
  /**
   * One job of the run — a provider×detector, a side product. A run fans out into several,
   * and "why did the Lantmäteriet detection fail" is a question about one of them.
   */
  jobId?: string | null;
}

export interface ExternalView {
  key: string;
  label: string;
  /** One line, shown as a tooltip/subtitle — what this view answers that the others do not. */
  hint: string;
  url: string;
}

/**
 * Everything below DEBUG, dropped.
 *
 * The pipeline logs a debug line per block, per window and per decision — thousands for one
 * course — and a link that opens on all of them buries the dozen INFO lines that tell the
 * story. Written as an exclusion rather than `severity_text:(INFO or ...)` on purpose: a record
 * that arrived without a severity is kept rather than silently dropped from every view.
 *
 * The one view that does not apply it is "With debug", which exists precisely to see them.
 */
const INFO_AND_ABOVE = 'not severity_text:(DEBUG or TRACE)';

/**
 * Where a line was written and what it said. An analysis crosses geodata, the batch worker
 * and the analyser, and `attributes.Where` (the worker or provider a job was dispatched on)
 * is what tells their lines apart in one view. Its values are short, so it gets a fixed
 * width rather than the one Discover would size for the longest one.
 */
const LOG_COLUMNS = ['attributes.Where', 'body.text'];

const LOG_COLUMN_WIDTHS = { 'attributes.Where': 239 };

const kibanaUrl = (query: string): string =>
  buildDiscoverUrl({ query, lookback: LOG_LOOKBACK, columns: LOG_COLUMNS, columnWidths: LOG_COLUMN_WIDTHS });

/** Base URL of the Grafana to link to, without a trailing slash. */
export const getGrafanaBaseUrl = (): string =>
  (process.env.EXPO_PUBLIC_GRAFANA_BASE_URL
    || (isProdBackend() ? PROD_GRAFANA_BASE_URL : DEV_GRAFANA_BASE_URL)).replace(/\/+$/, '');

const grafanaUrl = (
  dashboard: { uid: string; slug: string },
  vars: Record<string, string> = {},
): string => {
  const params = Object.entries(vars)
    .map(([name, value]) => `var-${name}=${encodeURIComponent(value)}`)
    .join('&');
  const query = params ? `?${params}` : '';
  return `${getGrafanaBaseUrl()}/d/${dashboard.uid}/${dashboard.slug}${query}`;
};

/**
 * Kibana views for one course's analyses, most specific first.
 *
 * The first is the one worth having: a correlation id ties every message and every log line of
 * one analysis together across geodata, the batch worker and the analyser, so it answers "show
 * me everything that happened for this run" in a single filter. The rest are the fallbacks for
 * when that is not enough — or, for an older run, not there.
 */
export const analysisLogViews = (target: AnalysisLinkTarget): ExternalView[] => {
  const views: ExternalView[] = [];
  const { courseId, runId, correlationId, jobId } = target;

  if (jobId) {
    // Narrower than the correlation id: every service puts the job id on the lines it
    // writes for one job — geodata and the worker as a log scope, the analyser from the
    // headers the worker sends it — so this is "this job, wherever it ran".
    views.push({
      key: 'job',
      label: 'This job',
      hint: 'Every line of this one job, on every service',
      url: kibanaUrl(`attributes.JobId:"${jobId}" and ${INFO_AND_ABOVE}`),
    });
  }

  if (correlationId) {
    views.push({
      key: 'correlation',
      label: 'This analysis',
      hint: 'Every service, every step of the latest run, on one thread',
      url: kibanaUrl(`attributes.CorrelationId:"${correlationId}" and ${INFO_AND_ABOVE}`),
    });
  }

  if (runId) {
    views.push({
      key: 'run',
      label: 'Latest run',
      hint: 'Lines naming this run id, wherever they were written',
      url: kibanaUrl(
        `(attributes.RunId:"${runId}" or body.text:"${runId}") and ${INFO_AND_ABOVE}`,
      ),
    });
  }

  views.push({
    key: 'course',
    label: 'This course',
    hint: 'Every analysis, acquisition and flyover for the course',
    url: kibanaUrl(
      `(attributes.CourseId:"${courseId}" or body.text:"${courseId}") and ${INFO_AND_ABOVE}`,
    ),
  });

  views.push({
    key: 'problems',
    label: 'Problems only',
    // severity_text, not log.level: these logs stay OpenTelemetry-native end to end and are
    // never normalised into ECS field names. See CLAUDE.md's observability rule.
    hint: 'Warnings and errors for this course, nothing else',
    url: kibanaUrl(
      `(attributes.CourseId:"${courseId}" or body.text:"${courseId}") and severity_text:(WARN or ERROR or FATAL)`,
    ),
  });

  views.push({
    key: 'analyser',
    label: 'Analyser only',
    hint: 'What the Python analyser itself said — imagery, blocks, detection',
    url: kibanaUrl(
      `resource.attributes.service.name:"course-analysis-service" and body.text:"${courseId}"`
      + ` and ${INFO_AND_ABOVE}`,
    ),
  });

  // Last, and deliberately not the default. Debug is a line per block and per decision —
  // the detail you want once you already know which run to look at, and noise before that.
  views.push({
    key: 'debug',
    label: 'With debug',
    hint: 'Everything including debug — thousands of lines, for when the story is not enough',
    url: kibanaUrl(
      correlationId
        ? `attributes.CorrelationId:"${correlationId}"`
        : `attributes.CourseId:"${courseId}" or body.text:"${courseId}"`,
    ),
  });

  return views;
};

/** Grafana dashboards for one course's analyses, most specific first. */
export const analysisMetricViews = (target: AnalysisLinkTarget): ExternalView[] => {
  const views: ExternalView[] = [];

  if (target.runId) {
    views.push({
      key: 'run',
      label: 'Latest analysis',
      hint: 'Every stage, counter and candidate of the newest run',
      url: grafanaUrl(GRAFANA_DASHBOARDS.run, { run_id: target.runId }),
    });
  }

  views.push({
    key: 'course',
    label: 'This course',
    hint: 'Every analysis this course has had, and what each one changed',
    url: grafanaUrl(GRAFANA_DASHBOARDS.course, { course_id: target.courseId }),
  });

  views.push({
    key: 'overview',
    label: 'All courses',
    hint: 'The estate: throughput, failures, imagery reuse, features published',
    url: grafanaUrl(GRAFANA_DASHBOARDS.overview),
  });

  return views;
};
